import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { debounce } from "lodash";
import moment from "moment";
import {
  cancelLogisticsOrder,
  getLogiscticsTodayOrders,
  getLogisticsSummary,
  refreshLogisticShipmentDetails,
} from "../../../config/Services";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import DeliveryHistory from "./components/DeliveryHistory";
import NewBooking from "./components/NewBooking";
import OrderDetailsCard from "./components/OrderDetailsCard";
import "./logistics.css";

export default function ThirdPartyLogistics() {
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 0;
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");
  // const debouncedSearchValue = useDebounce(searchValue, 300);
  const debouncedFetchOrders = useMemo(
    () =>
      debounce((value) => {
        fetchLogisticsOrders(initialPage, value);
      }, 300),
    []
  );

  const [summary, setSummary] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [refreshLoader, setRefreshLoader] = useState({});
  const [selectedCompTab, setSelectedCompTab] = useState("Active");
  const [isOpen, setIsOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [cancelLogisticsLoader, setCancelLogisticsLoader] = useState({});

  const [pageState, setPageState] = useState({
    page: initialPage.toString(),
    pageSize: 10,
    totalResults: 0,
    totalPages: 0,
    data: [],
    loader: false,
  });

  const deliveryTabs = useMemo(
    () => [{ label: "Active" }, { label: "Scheduled" }, { label: "History" }],
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "data",
        headerName: "",
        flex: 1,
        renderCell: (params) => {
          return (
            <OrderDetailsCard
              params={params}
              handleRefresh={refreshShipmentDetails}
              refreshLoader={refreshLoader[params?.row?.shipmentId]}
              cancelLogistics={cancelLogistics}
              cancelLogisticsLoader={cancelLogisticsLoader[params?.row?.shipmentId]}
            />
          );
        },
      },
    ],
    [pageState, refreshLoader, cancelLogisticsLoader]
  );

  // Page change handler
  const handlePageChange = (newPage) => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", newPage);
    setSearchParams(updatedParams);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // functions
  const fetchLogisticsOrders = async (page, value) => {
    try {
      setPageState((prev) => ({ ...prev, loader: true }));

      const payload = {
        locationId: locId,
        organizationId: orgId,
        status: selectedCompTab === "Active" ? "" : selectedCompTab.toUpperCase(),
        search: value ? value : searchValue,
        pageNumber: page ? page : pageState?.page,
        pageSize: 10,
      };
      const response = await getLogiscticsTodayOrders(payload);

      if (response?.data?.data?.es) {
        showSnackbar("Something went wrong", "error");
      } else {
        setPageState((prev, indx) => ({
          ...prev,
          page,
          id: indx,
          data: response?.data?.data?.orders || [],
          totalResults: response?.data?.data?.totalOrders || 0,
          totalPages: response?.data?.data?.totalPages || 0,
          loader: false,
        }));
      }
    } catch (err) {
      showSnackbar("Something went wrong", "error");
    } finally {
      setPageState((prev) => ({
        ...prev,
        loader: false,
      }));
    }
  };

  const fetchLogisticsSummary = async () => {
    try {
      setLoadingSummary(true);
      const payload = {
        locationId: locId,
        organizationId: orgId,
      };

      const response = await getLogisticsSummary(payload);

      if (response?.data?.data?.es) {
        setLoadingSummary(false);
        return;
      }

      let responseData = response?.data?.data;
      const summaryData = {
        totalOrders: responseData?.summary?.TOTAL ?? "N/A",
        pendingOrders: responseData?.summary?.PENDING ?? "N/A",
        readyForPickup: responseData?.summary?.READY_FOR_PICKUP ?? "N/A",
        shipped: responseData?.summary?.SHIPPED ?? "N/A",
        completed: responseData?.summary?.COMPLETED ?? "N/A",
        cancelled: responseData?.summary?.CANCELLED ?? "N/A",
        lastUpdated: responseData?.updated
          ? moment.utc(responseData?.updated).local().format("D MMM YYYY, h:mm a")
          : "N/A",
      };

      setSummary(summaryData);
      setLoadingSummary(false);
    } catch (err) {
      showSnackbar("Something went wrong", "error");
      setLoadingSummary(false);
    } finally {
      setLoadingSummary(false);
    }
  };

  // shipment refreshed Data
  const refreshShipmentDetails = async (shipmentId) => {
    if (refreshLoader[shipmentId]) return;

    try {
      setRefreshLoader((prev) => ({ ...prev, [shipmentId]: true }));
      const response = await refreshLogisticShipmentDetails(shipmentId);

      if (response?.data?.data?.es) {
        showSnackbar("Something went wrong", "error");
        return;
      }
      const shipmentData = response?.data?.data?.shipment;

      // Update only the matched shipment in pageState.data
      setPageState((prev) => ({
        ...prev,
        data: prev?.data?.map((item) => (item?.shipmentId === shipmentId ? shipmentData : item)),
      }));
    } catch (err) {
      showSnackbar("Something went wrong", "error");
    } finally {
      setRefreshLoader((prev) => ({ ...prev, [shipmentId]: false }));
    }
  };

  // cancel shipment
  const cancelLogistics = async (shipmentId, orderId, userId) => {
    setCancelLogisticsLoader((prev) => ({ ...prev, [shipmentId]: true }));
    try {
      const payload = {
        shipmentId: shipmentId || "",
        orderId: orderId || "",
        userId: userId || "",
      };
      const response = await cancelLogisticsOrder(payload);
      if (response?.data?.data?.es) {
        showSnackbar(response?.data?.data?.message || "Failed to Cancel Shipment", "error");
        fetchLogisticsOrders(pageState?.page);
      } else {
        showSnackbar("Shipment cancelled successfully", "success");
        // Refresh the list
        fetchLogisticsOrders(pageState?.page);
      }
    } catch (err) {
      showSnackbar("Something went wrong", "error");
    } finally {
      setCancelLogisticsLoader((prev) => ({ ...prev, [shipmentId]: false }));
    }
  };

  // useEffect(() => {
  //   if (selectedCompTab === "Active" || selectedCompTab === "Scheduled") {
  //     fetchLogisticsOrders(initialPage);
  //   }
  //   if (refresh) {
  //     fetchLogisticsOrders(initialPage);
  //     setRefresh(false);
  //   }
  // }, [selectedCompTab, refresh]);
  useEffect(() => {
    fetchLogisticsSummary();
    fetchLogisticsOrders(initialPage);
  }, []);

  const prevTabRef = useRef(selectedCompTab);

  useEffect(() => {
    if (prevTabRef.current === selectedCompTab) return; // skip mount
    prevTabRef.current = selectedCompTab;
    fetchLogisticsOrders(0);
  }, [selectedCompTab]);

  useEffect(() => {
    if (!refresh) return; // only runs when refresh flips to true
    fetchLogisticsOrders(initialPage);
    setRefresh(false);
  }, [refresh]);

  useEffect(() => {
    return () => {
      debouncedFetchOrders.cancel();
    };
  }, [debouncedFetchOrders]);

  // useEffect(() => {
  //   fetchLogisticsSummary();
  // }, []);

  const summaryCards = [
    { label: "Total Orders", key: "totalOrders", bg: "#f1f5f9", color: "#475569" },
    { label: "Pending Orders", key: "pendingOrders", bg: "#fef3c7", color: "#b45309" },
    { label: "Ready for Pickup", key: "readyForPickup", bg: "#dbeafe", color: "#1d4ed8" },
    { label: "Shipped", key: "shipped", bg: "#fce7f3", color: "#be185d" },
    { label: "Completed", key: "completed", bg: "#d1fae5", color: "#047857" },
    { label: "Cancelled", key: "cancelled", bg: "#ffe4e6", color: "#be123c" },
  ];

  return (
    <div className="logistics-screen-layout">
      {/* ── Header ── */}
      <section className="logistics___header">
        <h1 className="logistics___header-title">Logistics Dashboard</h1>
        <button className="logistics___create-btn" onClick={handleOpen}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          Create Delivery Request
        </button>
      </section>

      {/* ── Scrollable area ── */}
      <div className="logistics___scroll-area">
        {/* ── Summary ── */}
        <div className="logistics___summary-wrapper">
          <div className="logistics___summary-header">
            <h2 className="logistics___summary-heading">Order Status Overview</h2>
            {summary?.lastUpdated && (
              <span className="logistics___summary-updated">
                Last Updated on {summary.lastUpdated}
              </span>
            )}
          </div>
          <div className="logistics___summary-grid">
            {summaryCards.map((card) => (
              <div
                key={card.label}
                className="logistics___summary-card"
                style={{ background: card.bg, color: card.color }}
              >
                <span className="logistics___summary-card-label">{card.label}</span>
                <span className="logistics___summary-card-value">
                  {summary?.[card.key] ?? "N/A"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Tabs + Search ── */}
        <div className="logistics___tabs-search-row">
          <div className="logistics___tabs-container">
            {deliveryTabs?.map((ele) => (
              <button
                key={ele?.label}
                className={`logistics___tab-btn ${
                  selectedCompTab === ele?.label ? "logistics___tab-btn-active" : ""
                }`}
                onClick={() => setSelectedCompTab(ele?.label)}
              >
                {ele?.label}
              </button>
            ))}
          </div>

          {selectedCompTab !== "History" ? (
            <div className="logistics___search-filter-group">
              <div className="logistics___search-wrapper">
                <MagnifyingGlassIcon className="logistics___search-icon" aria-hidden="true" />
                <input
                  className="logistics___search-input"
                  type="text"
                  placeholder="Search ID, Name, Phone..."
                  value={searchValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updatedSearchParams = new URLSearchParams(searchParams);
                    if (value) {
                      updatedSearchParams.set("search", value);
                    } else {
                      updatedSearchParams.delete("search");
                    }
                    updatedSearchParams.set("page", "0");
                    setSearchParams(updatedSearchParams);
                    setSearchValue(value);
                    setPageState((prev) => ({ ...prev, page: 0 }));
                    debouncedFetchOrders(value);
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* ── Order list / History ── */}
        {["Active", "Scheduled"].includes(selectedCompTab) ? (
          <DataGrid
            rows={pageState?.data || []}
            columns={columns}
            getRowId={(row) => row?.shipmentId}
            loading={pageState?.loader}
            rowCount={parseInt(pageState?.totalResults)}
            pagination
            page={pageState?.page}
            pageSize={pageState?.pageSize}
            paginationMode="server"
            onPageChange={handlePageChange}
            disableSelectionOnClick
            disableColumnMenu
            autoHeight
            getRowHeight={() => "auto"} // Add this line
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": {
                padding: "10px", // Remove default cell padding if needed
                borderBottom: "none",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
            headerHeight={0}
          />
        ) : selectedCompTab === "History" ? (
          <DeliveryHistory
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            showSnackbar={showSnackbar}
            orgId={orgId}
            locId={locId}
            debouncedSearchValue={searchValue}
          />
        ) : null}
      </div>

      {/* new booking drawer */}
      {isOpen && <NewBooking handleClose={handleClose} setRefresh={setRefresh} />}
    </div>
  );
}
